import React, { useState, useEffect, useMemo } from 'react';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { TrendCompare, Metric } from '../components/SharedComponents';
import { tkr, raStartYear } from '../utils';
import { getLedningData, getLanthandel } from '../qlik';
import { BOLAG, RADER, raManader, MANAD_LABELS, summering } from './budgetModel';

// ── BUDGET: DIAGRAM (presentation) ────────────────────────────────
// Budget vs utfall per månad + resultat-/likviditetskurvor för vald
// budgetversion. Utfall: Qlik (ledning-cachen) för Hotell AB & SC AB;
// Stigmyhrs utfall = Lanthandeln (kiosken) tills Fortnox-kopplingen finns.

const ACCENT = '#44365C';
const btn = (active) => ({
  background: active ? ACCENT : 'transparent', color: active ? '#fff' : 'var(--text-secondary)',
  border: '1px solid var(--border)', borderRadius: 20, padding: '4px 12px',
  fontSize: 11.5, cursor: 'pointer', fontFamily: 'system-ui,sans-serif',
});

function raVal() {
  const y = raStartYear();
  const mk = (s) => `${String(s % 100).padStart(2, '0')}${String((s + 1) % 100).padStart(2, '0')}`;
  return [mk(y + 1), mk(y), mk(y - 1)];
}
const raLabel2 = (ra) => `RÅ ${ra.slice(0, 2)}/${ra.slice(2)}`;

export default function BudgetDiagram() {
  const [ra, setRa] = useState(raVal()[1]);
  const [bolag, setBolag] = useState('hotell');
  const [versioner, setVersioner] = useState([]);
  const [version, setVersion] = useState('B0');
  const [budget, setBudget] = useState(null);   // dokumentets rader
  const [utfall, setUtfall] = useState(null);   // ym → { intakt }

  const manader = useMemo(() => raManader(ra), [ra]);
  const raderDef = RADER[bolag];

  useEffect(() => {
    let live = true;
    (async () => {
      const snap = await getDocs(query(collection(db, 'intel_budget'), where('bolag', '==', bolag), where('ra', '==', ra)));
      // B0 först, sedan P-versionerna i nummerordning; öppna den senaste
      const nr = (v) => (v === 'B0' ? -1 : Number(String(v).replace(/^\D+/, '')) || 0);
      const vs = snap.docs.map((d) => d.data().version).sort((a, b) => nr(a) - nr(b) || a.localeCompare(b));
      if (!live) return;
      setVersioner(vs);
      setVersion(vs.length ? vs[vs.length - 1] : 'B0');
    })().catch(console.error);
    return () => { live = false; };
  }, [bolag, ra]);

  useEffect(() => {
    let live = true;
    getDoc(doc(db, 'intel_budget', `${bolag}_${ra}_${version}`))
      .then((s) => { if (live) setBudget(s.exists() ? s.data().rader || {} : {}); })
      .catch(console.error);
    return () => { live = false; };
  }, [bolag, ra, version]);

  // Utfall: intäkter per månad för bolagets enheter
  useEffect(() => {
    let live = true;
    (async () => {
      const perMan = {};
      if (bolag === 'stigmyhrs') {
        const lh = await getLanthandel();
        for (const [datum, oms] of (lh?.days || [])) {
          const ym = datum.slice(0, 4) + datum.slice(5, 7);
          perMan[ym] = (perMan[ym] || 0) + oms;
        }
      } else {
        const rows = await getLedningData(); // [{arManad, enhet, intakt, kostnad}]
        const enhSet = new Set(raderDef.filter((r) => r.grupp === 'intakt' && r.qlikEnhet).map((r) => r.qlikEnhet));
        for (const r of rows) {
          if (!enhSet.has(r.enhet)) continue;
          const ym = String(r.arManad);
          perMan[ym] = (perMan[ym] || 0) + (r.intakt || 0);
        }
      }
      if (live) setUtfall(perMan);
    })().catch(console.error);
    return () => { live = false; };
  }, [bolag, raderDef]);

  const S = useMemo(() => budget ? summering(budget, raderDef, manader) : null, [budget, raderDef, manader]);

  const D = useMemo(() => {
    if (!S || !utfall) return null;
    // Intäkter: budget vs utfall (utfall bara för månader som passerat/pågår)
    const nu = new Date();
    const nuYm = `${nu.getFullYear()}${String(nu.getMonth() + 1).padStart(2, '0')}`;
    const intakter = manader.map((m, i) => ({
      label: MANAD_LABELS[i],
      value: m <= nuYm ? Math.round(utfall[m] || 0) : null, // utfall
      prev: S[m].intakter || null,                           // budget
    }));
    let ackB = 0, ackU = 0;
    const ackIntakt = manader.map((m, i) => {
      ackB += S[m].intakter;
      const har = m <= nuYm;
      if (har) ackU += utfall[m] || 0;
      return { label: MANAD_LABELS[i], value: har ? Math.round(ackU) : null, prev: Math.round(ackB) };
    });
    let ackR = 0, ackL = 0;
    const resultat = manader.map((m, i) => {
      ackR += S[m].resultat; ackL += S[m].likvidResultat;
      return { label: MANAD_LABELS[i], value: Math.round(ackR), prev: Math.round(ackL) };
    });
    // KPI:er hittills (t.o.m. senaste passerade månad med utfall)
    const passerade = manader.filter((m) => m <= nuYm && (utfall[m] || 0) > 0);
    const budgHittills = passerade.reduce((s, m) => s + S[m].intakter, 0);
    const utfHittills = passerade.reduce((s, m) => s + (utfall[m] || 0), 0);
    return { intakter, ackIntakt, resultat, budgHittills, utfHittills, antalMan: passerade.length };
  }, [S, utfall, manader]);

  const bolagNamn = BOLAG.find((b) => b.id === bolag)?.namn || '';
  const diffPct = D && D.budgHittills > 0 ? Math.round((D.utfHittills - D.budgHittills) / D.budgHittills * 100) : null;

  return (
    <>
      <div className="card" style={{ paddingTop: 12, paddingBottom: 12 }}>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', fontFamily: 'system-ui,sans-serif' }}>
          {raVal().map((r) => <button key={r} onClick={() => setRa(r)} style={btn(ra === r)}>{raLabel2(r)}</button>)}
          <div style={{ width: 1, alignSelf: 'stretch', background: 'var(--border)', margin: '0 4px' }} />
          {BOLAG.map((b) => <button key={b.id} onClick={() => setBolag(b.id)} style={btn(bolag === b.id)}>{b.namn.replace(' AB', '')}</button>)}
          <div style={{ width: 1, alignSelf: 'stretch', background: 'var(--border)', margin: '0 4px' }} />
          {(versioner.length ? versioner : ['B0']).map((v) => (
            <button key={v} onClick={() => setVersion(v)} style={btn(version === v)}>{v}</button>
          ))}
        </div>
      </div>

      {!D && <div className="card"><div style={{ fontSize: 13, color: 'var(--text-secondary)', fontFamily: 'system-ui,sans-serif' }}>Laddar…</div></div>}
      {D && (
        <>
          {D.antalMan > 0 && (
            <div className="grid-3">
              <Metric label={`Utfall hittills (${D.antalMan} mån)`} value={tkr(D.utfHittills)} sub="Intäkter enligt Qlik" />
              <Metric label="Budget samma period" value={tkr(D.budgHittills)} sub={`${version}`} />
              <Metric label="Mot budget" value={diffPct !== null ? `${diffPct >= 0 ? '+' : ''}${diffPct}%` : '–'}
                trend={diffPct !== null ? (diffPct >= 0 ? 'up' : 'down') : ''} sub="Intäkter, ack. avvikelse" />
            </div>
          )}

          <div className="card">
            <div className="card-title">Intäkter per månad — utfall vs budget · {bolagNamn} · {raLabel2(ra)} ({version})</div>
            <TrendCompare data={D.intakter} formatVal={(v) => tkr(v)} curColor={ACCENT} prevColor="#C2A878" curLabel="Utfall" prevLabel="Budget" />
            <div className="note">Utfall: {bolag === 'stigmyhrs' ? 'HonestBox (Lanthandeln — huvudbutiken tillkommer via Fortnox)' : 'Qlik'} · budget = vald version.</div>
          </div>

          <div className="card">
            <div className="card-title">Ackumulerade intäkter — utfall vs budget</div>
            <TrendCompare data={D.ackIntakt} formatVal={(v) => tkr(v)} curColor={ACCENT} prevColor="#C2A878" curLabel="Utfall ack." prevLabel="Budget ack." />
          </div>

          <div className="card">
            <div className="card-title">Budgeterat resultat & likviditet — ackumulerat över året</div>
            <TrendCompare data={D.resultat} formatVal={(v) => tkr(v)} curColor="#1D9E75" prevColor="#A32D2D" curLabel="Resultat ack." prevLabel="Likvid ack." />
            <div className="note">Likvid ack. = driftsresultat − räntor − amorteringar ± extraordinära poster, ackumulerat — styrelsens likviditetskurva.</div>
          </div>
        </>
      )}
    </>
  );
}
